/**
 * Advisories carried on a **successful** response.
 *
 * ## Why this exists at all, rather than being a `detail` string on the response
 *
 * `Decisions/2026-08-04 The Default Slot` ends with a rule that is a correctness
 * requirement rather than UX polish: *any* finer-grained link intended to change where
 * plain scans land must occupy its grain's default slot, and **silence is wrong under
 * every reading of the rule.** The dashboard asks the question at creation time (c35). An
 * integrator posting to the API directly gets no dialog — so the API answers the same
 * question in the response, and it has to be a *machine-readable* member rather than
 * prose, or an integrator can only act on it by matching English.
 *
 * ## Deliberately shaped like `problem.ts`, and deliberately not the same thing
 *
 * Same guarantee: the registry is keyed by the code and {@link WarningCode} is derived
 * from those keys, so **renaming a code is a compile error rather than a silent wire
 * change**. A warning code is on the wire exactly as much as a problem code is —
 * integrators branch on it, support replies quote it.
 *
 * What is **not** copied is the RFC 9457 envelope. There is no `type` URI and no `status`.
 * RFC 9457 describes a *problem* — a response that failed — and a warning rides on a 200 or
 * a 201 where nothing failed. Minting `https://packagepage.com/problems/…` URIs for
 * successful responses would say the request went wrong when it did not.
 */
/**
 * Every advisory this platform can attach to a successful response.
 *
 * `title` lives with the code because it is a property *of* it. `detail` is supplied per
 * occurrence and is the only place a value from the request may appear — the same division
 * `problem.ts` makes, for the same reason.
 */
export const WARNINGS = {
    /**
     * A link was created at a finer grain than the GTIN, without the default flag.
     *
     * The naive expectation is that a lot-level link moves lot-level scans. It does not:
     * shadowing is per link type, and the default slot is the only mover of plain scans. So
     * the link exists, the dashboard shows it, and a phone camera never reaches it — the
     * same failure shape §3.8 guards for recalls, with nothing guarding it for a brand
     * creating a lot-level advisory.
     */
    DEFAULT_SLOT_UNCLAIMED: {
        title: "Plain scans will not land on this link",
    },
    /**
     * A recall was published against a product that is not published, so **nothing is
     * mirrored and no scan resolves to the notice yet** (c32).
     *
     * The write is correct and is deliberately not refused. A draft product's GTIN 404s from
     * the resolver honestly because nothing was ever mirrored, §8.1 blocks artwork generation
     * for a draft product so no code can have been printed, and the recall's rows are
     * ordinary `links` rows that take effect the moment the product is published. Refusing
     * would be gating a safety action, which invariant 3 forbids outright.
     *
     * **What was wrong was the silence.** `emitResolutionChange` returns
     * `{emitted: false, reason: "draft"}` rather than raising — correct for a link edit on a
     * draft, and wrong to pass over on a recall, because a recall's entire purpose is to
     * change what a scan resolves to. A brand that publishes one and is told nothing has
     * every reason to believe consumers are being warned. So the response says so, in a
     * member an integrator can branch on, and the `recall_events` payload records it too —
     * an evidence trail that logged a publish without recording that nothing was mirrored
     * would be a trail that is false in the one direction that matters.
     */
    RECALL_NOT_RESOLVING: {
        title: "This recall is recorded but not yet resolving",
    },
    // ---- §8.1's warn-only set (c91) -----------------------------------------
    //
    // §8.1 lists six blocking rows and then, separately, an **explicitly warn-only** set:
    // *"ever-live domain currently unhealthy · X-dimension above the recommended range ·
    // ECC level choices · symbol colour within a validated pair."* The first two are the
    // ones with a measurable trigger, so they are the two that get codes; ECC level and
    // colour pair are choices with no failing value to warn about yet.
    //
    // **They are warnings on a 200, and nothing lifts them into a refusal.** That is the
    // structural half of §8.1's claim, and it is worth stating where the codes live because
    // it is asserted by absence: the artwork gate path takes **no tier, plan or billing
    // argument anywhere**, so *"no paid plan lifts it"* is true by construction rather than
    // by a branch nobody took. `packages/barcode`'s watermark policy makes the same argument
    // the same way — the absence of a flag is the guarantee.
    /**
     * §8.1's custom-domain predicate, middle rung: `first_live_at IS NOT NULL` **and** the
     * click-time probe on this Host failed.
     *
     * §8.1 writes this rung as *"WARN + TYPED CONFIRM"* and then lists it in the warn-only
     * set, and the two are not in tension once you see where each lives. **The typed
     * confirmation is the screen's** — `screens.md` §6 puts it there in those words,
     * *"typed confirmation, not a block"* — and the API's half is this warning, because an
     * API that refused until an acknowledgement field arrived would be a block wearing a
     * different name, which is exactly what the row forbids.
     *
     * The reason the distinction is load-bearing rather than pedantic is in §8.1 itself:
     * blocking a momentarily-unhealthy domain *"manufactures the worst failure mode — a user
     * against a print deadline, blocked by a transient probe, taking the escape hatch and
     * permanently converting their codes to a platform URI over a network blip."* The
     * delegation is established and the customer demonstrably controls the name; what failed
     * is a request, which is a fact about right now.
     *
     * **In an environment with no edge the probe reports failure honestly**, so this warning
     * is what local development and CI see. That is the designed answer rather than a
     * degradation: the probe answers *"did this host serve this GTIN just now"*, and in a
     * tree with no edge running the truthful answer is no.
     */
    DOMAIN_EVER_LIVE_UNHEALTHY: {
        title: "This domain is live but did not answer just now",
    },
    /**
     * §8.1's *"X-dimension above the recommended range"* — `packages/barcode`'s
     * `XDIM_ABOVE_RECOMMENDED` verdict, on the wire.
     *
     * **Warn-only for a physical reason, and the direction is not symmetric with the block
     * below the minimum.** An undersized symbol does not scan at the lane and the labels
     * cannot be re-dimensioned; an oversized one scans perfectly well. What it costs is label
     * area, and whether that fits the pack is a judgement the person holding the artwork is
     * better placed to make than we are.
     *
     * The code is spelled as `x-dimension.ts` spells its verdict, which is §8.1's
     * SCREAMING_SNAKE convention applied to a member §8.1 never named — that section gives
     * codes to the six blocking rows only and states the warn-only set in prose. Recorded in
     * the Drift Log at c89, when the name was chosen; this is where it reaches a wire.
     */
    XDIM_ABOVE_RECOMMENDED: {
        title: "That module width is above the recommended range",
    },
    /**
     * The hostname claimed is a registrable apex — `brand.com` rather than `id.brand.com`.
     *
     * **Steered, not refused**, per §8.1's block-only-the-unrecoverable: pointing an apex at
     * us is undone by editing one DNS record, and refusing it outright would fail a customer
     * who typed the thing they own. `screens.md` §8's own state D says the same in copy — the
     * consequence they care about is their website going down, not the DNS technicality.
     *
     * **It exists because the screen is not the only client.** The dashboard renders the
     * steer, and a REST caller has nothing in front of it — an apex row created over the API
     * would otherwise sit in `pending_dns` forever with no explanation, since a bare domain
     * cannot carry a CNAME at all. §5's `warnings` array is the mechanism for exactly this: a
     * 201 that succeeded and still has something to say.
     */
    DOMAIN_APEX_STEERED: {
        title: "That is a whole domain, not a subdomain",
    },
};
/** Build the body. `detail` is required, for `problem()`'s reason. */
export function warning(code, detail) {
    return { code, title: WARNINGS[code].title, detail };
}
//# sourceMappingURL=warnings.js.map